import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// Default page permissions for fallback
const DEFAULT_PAGE_PERMISSIONS = {
  reports: "view",
  sensory_hotovky: "view",
  sensory_krabicky: "view",
  autoclave: "view",
  batch_search: "view",
  kitchen: "view",
  balirna: "view",
  admin: "none"
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper function to check page permission
export const hasPagePermission = (user, pageKey, requiredLevel = 'view') => {
  if (!user) return false;
  
  // Admin always has full access
  if (user.role === 'admin' || (user.permissions && user.permissions.includes('admin'))) {
    return true;
  }
  
  const permissions = user.page_permissions || DEFAULT_PAGE_PERMISSIONS;
  const userLevel = permissions[pageKey] || 'none';
  
  // Permission hierarchy: edit > view > none, denied is special (visible but blocked)
  if (userLevel === 'denied') return false;
  if (userLevel === 'none') return false;
  if (requiredLevel === 'view') return userLevel === 'view' || userLevel === 'edit';
  if (requiredLevel === 'edit') return userLevel === 'edit';
  
  return false;
};

// Check if page should be visible in menu
export const isPageVisible = (user, pageKey) => {
  if (!user) return false;
  
  // Admin always sees everything
  if (user.role === 'admin' || (user.permissions && user.permissions.includes('admin'))) {
    return true;
  }
  
  const permissions = user.page_permissions || DEFAULT_PAGE_PERMISSIONS;
  const userLevel = permissions[pageKey] || 'none';
  
  // Show in menu if not 'none'
  return userLevel !== 'none';
};

// Check if page access is denied (for showing "no permission" page)
export const isPageDenied = (user, pageKey) => {
  if (!user) return true;
  
  // Admin never denied
  if (user.role === 'admin' || (user.permissions && user.permissions.includes('admin'))) {
    return false;
  }
  
  const permissions = user.page_permissions || DEFAULT_PAGE_PERMISSIONS;
  return permissions[pageKey] === 'denied';
};

// Check if user can edit (for hiding edit buttons etc.)
export const canEdit = (user, pageKey) => {
  if (!user) return false;
  
  // Admin always can edit
  if (user.role === 'admin' || (user.permissions && user.permissions.includes('admin'))) {
    return true;
  }
  
  const permissions = user.page_permissions || DEFAULT_PAGE_PERMISSIONS;
  return permissions[pageKey] === 'edit';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};